/**
 * PublicLandingPage — renders a creator's published landing page.
 *
 * Route: /p/:handle/:slug
 * Fetches GET /api/public/page/{handle}/{slug} and renders sections.
 * Includes a buyer-facing "Buy now" → POST /api/public/page/{handle}/{slug}/checkout.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

function Section({ s }) {
  const k = s.kind;
  if (k === "hero") {
    return (
      <section className="text-center max-w-3xl mx-auto py-10" data-testid="public-section-hero">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ink leading-tight">{s.headline}</h1>
        {s.subheadline && <p className="mt-4 text-lg text-meta">{s.subheadline}</p>}
      </section>
    );
  }
  if (k === "value") {
    return (
      <section className="surface p-8 max-w-3xl mx-auto" data-testid="public-section-value">
        <h2 className="font-display text-2xl font-bold mb-4 text-ink">{s.title}</h2>
        <ul className="space-y-2">
          {(s.bullets || []).map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-ink-soft"><Sparkles className="h-4 w-4 text-ai mt-1 shrink-0" /> {b}</li>
          ))}
        </ul>
      </section>
    );
  }
  if (k === "transformation") {
    return (
      <section className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto" data-testid="public-section-transformation">
        <div className="surface p-6 border-rose-200/40">
          <p className="text-[11px] uppercase tracking-wider font-bold text-rose-500">Before</p>
          <p className="mt-2 text-ink-soft leading-relaxed">{s.before}</p>
        </div>
        <div className="surface p-6 border-emerald-200/40">
          <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-600">After</p>
          <p className="mt-2 text-ink-soft leading-relaxed">{s.after}</p>
        </div>
      </section>
    );
  }
  if (k === "faq") {
    return (
      <section className="max-w-3xl mx-auto" data-testid="public-section-faq">
        <h2 className="font-display text-2xl font-bold mb-4 text-ink">FAQ</h2>
        <div className="space-y-2">
          {(s.items || []).map((it, i) => (
            <details key={i} className="surface p-4">
              <summary className="cursor-pointer font-semibold text-ink">{it.q}</summary>
              <p className="mt-2 text-ink-soft">{it.a}</p>
            </details>
          ))}
        </div>
      </section>
    );
  }
  if (k === "testimonials") {
    return (
      <section className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-3" data-testid="public-section-testimonials">
        {(s.items || []).map((it, i) => (
          <blockquote key={i} className="surface p-5">
            <p className="text-ink-soft italic">"{it.quote}"</p>
            <p className="mt-2 text-sm font-semibold text-ink">— {it.name}</p>
          </blockquote>
        ))}
      </section>
    );
  }
  if (k === "cta") {
    return (
      <section className="text-center max-w-3xl mx-auto py-8" data-testid="public-section-cta">
        <p className="text-xl text-ink font-semibold">{s.text}</p>
      </section>
    );
  }
  return null;
}

function BuyPanel({ asset, handle, slug, onCheckout }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!asset) return null;

  const price = Number(asset.pricing?.price || 0);
  const currency = (asset.pricing?.currency || "USD").toUpperCase();

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email to continue"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/public/page/${handle}/${slug}/checkout`, {
        email, name, origin_url: window.location.origin,
      });
      onCheckout?.(r.data);
      if (r.data?.url) window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
    }
    setBusy(false);
  };

  return (
    <section className="max-w-md mx-auto" data-testid="public-buy-panel">
      <div className="surface p-6">
        <p className="text-xs uppercase tracking-wider text-meta font-bold">{asset.type?.replace("_", " ")}</p>
        <h3 className="font-display text-2xl font-bold text-ink mt-1">{asset.title}</h3>
        {asset.description && <p className="text-sm text-ink-soft mt-2 leading-relaxed">{asset.description}</p>}
        <p className="mt-4 font-display text-4xl font-extrabold text-ink">
          {currency === "USD" ? "$" : currency === "EUR" ? "€" : currency === "GBP" ? "£" : currency === "INR" ? "₹" : ""}{price}
          <span className="text-sm font-medium text-meta ml-2">{currency}</span>
        </p>
        <form onSubmit={submit} className="mt-4 space-y-2">
          <input
            type="text" placeholder="Your name (optional)"
            value={name} onChange={(e) => setName(e.target.value)}
            data-testid="public-buy-name"
            className="w-full rounded-xl bg-white border border-line px-3 py-2.5 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <input
            type="email" required placeholder="Email for receipt + access"
            value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="public-buy-email"
            className="w-full rounded-xl bg-white border border-line px-3 py-2.5 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <button
            type="submit" disabled={busy}
            data-testid="public-buy-btn"
            className="w-full mt-2 rounded-full ashra-gradient-bg px-5 py-3 text-sm font-semibold text-white inline-flex items-center justify-center gap-1 shadow-sm hover:shadow-md disabled:opacity-60 transition"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Buy now
          </button>
        </form>
        <p className="mt-3 text-[11px] text-meta inline-flex items-center gap-1">
          <ShieldCheck className="h-3 w-3" /> Secure checkout · instant access on payment
        </p>
      </div>
    </section>
  );
}

export default function PublicLandingPage() {
  const { handle, slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_BASE}/public/page/${handle}/${slug}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setNotFound(true); });
    return () => { alive = false; };
  }, [handle, slug]);

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAFA] p-8" data-testid="public-landing-notfound">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold text-ink">Page not found</h1>
          <p className="mt-2 text-meta">This landing page may be unpublished or deleted.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen grid place-items-center bg-[#FAFAFA]" data-testid="public-landing-loading"><Loader2 className="h-6 w-6 animate-spin text-ai" /></div>;
  }

  const { workspace, page, asset } = data;
  const sections = page.sections || [];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="public-landing-page">
      <header className="border-b border-line bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <p className="font-display text-lg font-bold text-ink">{workspace?.name || handle}</p>
          <p className="text-xs text-meta">Powered by Ashra AI</p>
        </div>
      </header>

      <main className="px-6 py-12 space-y-12">
        {sections.map((s, i) => <Section key={i} s={s} />)}
        <BuyPanel asset={asset} handle={handle} slug={slug} />
      </main>

      <footer className="border-t border-line py-8 text-center text-xs text-meta">
        © {new Date().getFullYear()} {workspace?.name || handle} · Built on Ashra AI
      </footer>
    </div>
  );
}
